import React from 'react';
import { Button, Dropdown, Input, Loader, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  AccountInfoType,
  ConfirmationType,
  OptionType,
  TimeZone,
} from 'shared/types';
import { getI18nLangCode, isValidColor, timeFormats } from 'shared/utils';
import { LANGUAGE_OPTIONS, timeZones } from '../../../../../shared/constants';
import {
  useGetAccountInfo,
  useGetAuthData,
  useUpdateAccountInfo,
} from '../../../../../shared/hooks';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
  FormSection,
} from '../../../../../shared/styles';
import { ColorPicker } from '../../../../components/ColorPicker';
import AccountActions from './components/account-actions';
import AccountDomain from './components/account-domain';
import UploadLogo from './components/uploadlogo';
import { SettingsFixedDrawer } from '../../styles';

const validUrlRegex =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

type AccountInfoProps = {
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

const AccountInfo = (props: AccountInfoProps) => {
  const { t, i18n } = useTranslation();
  const { setConfirmationData } = props;
  const [, setToast] = useToasts();
  const { data: accountInfo, isLoading } = useGetAccountInfo({});
  const { mutate: updateInfo, isLoading: isUpdating } = useUpdateAccountInfo({
    setToast: accountInfo?.timezone ? setToast : undefined,
    callback: (isSuccess: boolean, response: AccountInfoType) => {
      if (isSuccess) {
        const locale = getI18nLangCode(response.locale);
        if (locale !== i18n.language) {
          i18n.changeLanguage(locale);
        }
      }
    },
  });
  const { data: surveyAuthInfo, isLoading: isLoadingAuth } = useGetAuthData();

  const translatedTimeFormats = timeFormats.map((f) => ({
    ...f,
    label: `${t(f.label)}`,
  }));

  const form = useForm<AccountInfoType>({
    shouldFocusError: true,
    mode: 'onSubmit',
    resolver: yupResolver(
      Yup.object({
        name: Yup.string()
          .trim()
          .required(`${t('forms.err_msg.required_field')}`)
          .max(
            50,
            `${t('forms.err_msg.max_char', {
              len: '50',
            })}`,
          )
          .min(
            3,
            `${t('forms.err_msg.min_char', {
              len: '3',
            })}`,
          ),
        timezone: Yup.string()
          .trim()
          .required(`${t('forms.err_msg.required_field')}`)
          .typeError(`${t('forms.err_msg.required_field')}`),
        account_meta: Yup.object({
          brand_color: Yup.string()
            .trim()
            .required(`${t('forms.err_msg.required_field')}`)
            .test({
              test: (value, { createError }) => {
                if (!!value && !isValidColor(value)) {
                  return createError({
                    message: `${i18n.t('forms.err_msg.invalid_color')}`,
                  });
                }
                return true;
              },
            }),
          company_name: Yup.string()
            .trim()
            .required(`${t('forms.err_msg.required_field')}`)
            .max(
              50,
              `${t('forms.err_msg.max_char', {
                len: '50',
              })}`,
            )
            .min(
              3,
              `${t('forms.err_msg.min_char', {
                len: '3',
              })}`,
            ),
          logo_url: Yup.string()
            .nullable()
            .notRequired()
            .url('Enter valid URL')
            .when('account_meta.logo_url', {
              is: (val: string) => val?.length > 0,
              then: (rule) =>
                rule.matches(
                  validUrlRegex,
                  `${t('forms.err_msg.valid_logo_url')}`,
                ),
            }),
          favicon_url: Yup.string()
            .nullable()
            .notRequired()
            .url(`${t('forms.err_msg.valid_url')}`)
            .when('account_meta.favicon_url', {
              is: (val: string) => val?.length > 0,
              then: (rule) =>
                rule.matches(
                  validUrlRegex,
                  `${t('forms.err_msg.valid_favicon_url')}`,
                ),
            }),
        }),
      }),
    ),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty, submitCount, errors },
  } = form;

  const timeFormat = watch('time_format');
  const locale = watch('locale') || surveyAuthInfo?.user?.locale;

  const onSubmit = (val: AccountInfoType) => updateInfo(val);

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  React.useEffect(() => {
    if (!accountInfo?.freshid_account_id) return;
    reset(accountInfo);
  }, [accountInfo]);

  React.useEffect(() => {
    if (!accountInfo?.timezone) {
      onSubmit({
        ...getValues(),
        timezone: 'UTC',
      });
    }
  }, [accountInfo]);

  if (isLoading || isLoadingAuth)
    return (
      <Center height="calc(100vh - 100px)">
        <Loader />
      </Center>
    );

  return (
    <>
      <FormContainer>
        <FormHeader hideBorder>
          <div className="left-pane">
            <div>
              <div className="heading">
                {t('settings.account_info.heading')}
              </div>
              <div className="sub-heading">
                {t('settings.account_info.description')}
              </div>
            </div>
          </div>
        </FormHeader>
        <FormContent>
          <FormSection showBorder>
            <AccountDomain
              setConfirmationData={setConfirmationData}
              accountUrl={accountInfo?.domain}
              accountStatus={accountInfo?.status}
            />
          </FormSection>
          <FormSection maxWidth="512px">
            <FormField>
              <Input
                label={`${t('forms.labels.account_name')}`}
                {...register('name')}
                isErrored={!!get(errors, ['name', 'message'], '')}
                errorText={get(errors, ['name', 'message'], '')}
              />
            </FormField>
            <FormField>
              <UploadLogo
                id="logo-url"
                fieldId="account_meta.logo_url"
                form={form}
                label={`${t('forms.labels.logo_URL')}`}
                logoType="account-logo"
                recommendedSize="150 x 75"
                placeholder={`${t('settings.account_info.logo.upload_logo')}`}
              />
            </FormField>
            <FormField>
              <UploadLogo
                id="favicon-url"
                fieldId="account_meta.favicon_url"
                form={form}
                recommendedSize="16 x 16"
                label={`${t('forms.labels.favicon')}`}
                logoType="favicon-logo"
                placeholder={`${t(
                  'settings.account_info.logo.upload_favicon',
                )}`}
              />
            </FormField>
            <FormField>
              <ColorPicker
                color={watch('account_meta.brand_color') || ''}
                onChange={(newColor: string) =>
                  setFormValue('account_meta.brand_color', newColor)
                }
                isRequired
                label={`${t('forms.labels.brand_color')}`}
                isErrored={
                  submitCount > 0 &&
                  get(errors, ['account_meta', 'brand_color', 'message'], false)
                }
                errorText={get(
                  errors,
                  ['account_meta', 'brand_color', 'message'],
                  '',
                )}
              />
            </FormField>
            <FormField>
              <Input
                label={`${t('forms.labels.company_name')}`}
                {...register('account_meta.company_name')}
                isErrored={
                  submitCount > 0 &&
                  Boolean(
                    get(
                      errors,
                      ['account_meta', 'company_name', 'message'],
                      '',
                    ),
                  )
                }
                isRequired
                errorText={get(
                  errors,
                  ['account_meta', 'company_name', 'message'],
                  '',
                )}
              />
            </FormField>
            <FormField>
              <Dropdown
                {...register('timezone')}
                type="input"
                filterKey="timeZone"
                label={`${t('timezone')}`}
                isRequired
                emptyItemsText={`${t('no_results_found')}`}
                placeholder={`${t('forms.placeholders.account_timezone')}`}
                itemToString={(v: TimeZone) => `${v.timeZone} (${v.offset})`}
                itemComponent={(v: TimeZone) => `${v.timeZone} (${v.offset})`}
                defaultSelectedItem={
                  timeZones.find(
                    (item: TimeZone) => watch('timezone') === item.timeZone,
                  ) || null
                }
                onChange={(option: TimeZone) => {
                  setFormValue('timezone', option ? option.timeZone : null);
                }}
                isErrored={!!get(errors, ['timezone', 'message'], '')}
                errorText={get(errors, ['timezone', 'message'], '')}
                items={timeZones}
              />
            </FormField>
            <FormField>
              <Dropdown
                isRequired
                {...register('time_format')}
                defaultSelectedItem={
                  timeFormat
                    ? translatedTimeFormats.find(
                        (time: OptionType) => timeFormat === time.id,
                      )
                    : translatedTimeFormats[0]
                }
                emptyItemsText={`${t('no_results_found')}`}
                label={`${t('time_format')}`}
                itemToString={(v: OptionType) => v.label}
                itemComponent={(v: OptionType) => v.label}
                items={translatedTimeFormats}
                onChange={(option: OptionType) =>
                  setFormValue('time_format', option.id)
                }
              />
            </FormField>
            <FormField>
              <Dropdown
                isRequired
                {...register('locale')}
                label={`${t('language')}`}
                defaultSelectedItem={LANGUAGE_OPTIONS.find(
                  (l: OptionType) => l.id === locale,
                )}
                emptyItemsText={`${t('no_results_found')}`}
                itemToString={(v: OptionType) => v.label}
                itemComponent={(v: OptionType) => v.label}
                items={LANGUAGE_OPTIONS}
                onChange={(option: OptionType) =>
                  setFormValue('locale', option.id)
                }
                disabled={true}
              />
            </FormField>
          </FormSection>
        </FormContent>
        <FormActions>
          <div className="left-pane">
            <Button
              id="acc-info-save-btn"
              inline
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                handleSubmit(onSubmit, (err) => console.log('ERR', err))();
              }}
              disabled={!isDirty || isUpdating}
            >
              {t('save')}
            </Button>
          </div>
        </FormActions>
      </FormContainer>
      <SettingsFixedDrawer>
        <AccountActions setConfirmationData={setConfirmationData} />
      </SettingsFixedDrawer>
    </>
  );
};

export default AccountInfo;
